import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthCard, PrimaryAuthButton } from '@/components/AuthCard';
import { ApiError, api } from '@/lib/api';

/**
 * Lee `?token=...` del URL y permite definir una nueva contraseña.
 * Esta pantalla es pública (no requiere sesión).
 */
export function PasswordResetConfirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!token || token.length !== 64) {
    return (
      <AuthCard
        subtitle="Enlace no válido."
        meta={
          <Link
            to="/password/reset"
            className="text-[14px] font-medium text-icaria-600 hover:text-icaria-700"
          >
            Solicitar uno nuevo →
          </Link>
        }
      >
        <p className="text-center text-[14px] text-muted-foreground">
          El enlace está incompleto o no corresponde a una solicitud activa.
        </p>
      </AuthCard>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError('La contraseña y su confirmación no coinciden.');
      return;
    }
    if (newPassword.length < 10) {
      setError('La contraseña debe tener al menos 10 caracteres.');
      return;
    }
    setSubmitting(true);
    try {
      await api<void>('/auth/password/reset/confirm', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('No se ha podido conectar con el servidor.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthCard subtitle="Contraseña actualizada.">
        <p className="text-center text-[14px] text-muted-foreground">
          Te redirigimos al inicio de sesión…
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard subtitle="Define la contraseña con la que entrarás.">
      <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new">Nueva contraseña</Label>
          <Input
            id="new"
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <p className="text-[12px] text-muted-foreground">Mínimo 10 caracteres.</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirm">Confirmar nueva contraseña</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <PrimaryAuthButton submitting={submitting}>
          {submitting ? 'Guardando…' : 'Guardar y volver a entrar'}
        </PrimaryAuthButton>
      </form>
    </AuthCard>
  );
}
